import { ReactNode, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDraggable,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { v4 as uuid } from 'uuid';
import { TrashIcon, ArrowRightIcon, PlusIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';
import { Card } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import Badge from '@/components/ui/Badge';
import Switch from '@/components/ui/Switch';
import SegmentedControl from '@/components/ui/SegmentedControl';
import { getWorkouts } from '@/api/workoutsService';
import { CycleDetail, CycleSession, CycleSessionInput } from '@/api/cyclesService';
import { sportColor } from '@/lib/sportColors';
import { toaster } from '@/components/ui/toaster';
import { useFormats } from '@/features/reference/useReference';
import { useFormatGroups } from '@/features/reference/formatGroups';
import { useCycleSessions } from './useCycleSessions';

interface WorkoutInfo {
  id: string;
  name: string;
  sportIdentifier: string;
  themeLabel: string;
  durationSec: number | null;
  isTest: boolean;
}

interface Placement {
  uid: string;
  /** The saved `cycle_session` id; kept across moves so its ranks and generated sessions follow (D-c). */
  id?: string;
  weekNumber: number;
  dayOfWeek: number;
  workoutId: string;
  /** Insertion order inside the week, per plan format; the per-sport rank is derived from it (D22). */
  rankOrder: Record<string, number>;
  linkKey: string | null;
}

const SPORT_SHORT: Record<string, string> = { RUNNING: 'CAP', CYCLING: 'VÉLO', SWIMMING: 'NAT', PPG: 'PPG' };
const SPORT_LONG: Record<string, string> = {
  RUNNING: 'la course à pied',
  CYCLING: 'le vélo',
  SWIMMING: 'la natation',
  PPG: 'la PPG',
};
const PPG = 'PPG';

const isOptionalWorkout = (w: WorkoutInfo | undefined): boolean => Boolean(w && (w.isTest || w.sportIdentifier === PPG));

/**
 * Per-sport ranking of a week. D26: a test is ranked like any session, so the coach can rank it low
 * for a format that must not get it.
 *
 * R48: the enchaînement's two sessions are in here, each in **its own** sport. Their ranks decide
 * the brick's fate — no bike, no brick; bike but no run, the bike alone — so they can no longer sit
 * outside the ranking.
 */
const rankedOfSport = (
  placements: Placement[],
  info: Record<string, WorkoutInfo>,
  week: number,
  sport: string,
  formatId: string
): Placement[] =>
  placements
    .filter((p) => p.weekNumber === week && info[p.workoutId] && info[p.workoutId].sportIdentifier === sport)
    .sort((a, b) => (a.rankOrder[formatId] ?? 0) - (b.rankOrder[formatId] ?? 0) || a.uid.localeCompare(b.uid));

const rankOf = (
  placements: Placement[],
  info: Record<string, WorkoutInfo>,
  p: Placement,
  formatId: string
): { rank: number; total: number } | null => {
  const w = info[p.workoutId];
  if (!w) return null;
  const ranked = rankedOfSport(placements, info, p.weekNumber, w.sportIdentifier, formatId);
  return { rank: ranked.findIndex((x) => x.uid === p.uid) + 1, total: ranked.length };
};

/** The orders are linked when every format ranks every session the same. */
const sameOrderInAllFormats = (placements: Placement[], info: Record<string, WorkoutInfo>, formatIds: string[]): boolean =>
  placements.every((p) => {
    const ranks = formatIds.map((f) => rankOf(placements, info, p, f)?.rank);
    return ranks.every((r) => r === ranks[0]);
  });

/** A rank absent for a format (legacy data) reads last, as the engine reads it. */
const MISSING_RANK_OFFSET = 10_000;

/** One test per (week, sport): the admin UI refuses a second one. Brick members are never tests. */
const hasTestOfSport = (placements: Placement[], info: Record<string, WorkoutInfo>, week: number, sport: string, exceptUid?: string): boolean =>
  placements.some(
    (p) => p.uid !== exceptUid && p.weekNumber === week && !p.linkKey && info[p.workoutId]?.isTest && info[p.workoutId]?.sportIdentifier === sport
  );

const DAYS: { value: number; label: string }[] = [
  { value: 1, label: 'Lun' },
  { value: 2, label: 'Mar' },
  { value: 3, label: 'Mer' },
  { value: 4, label: 'Jeu' },
  { value: 5, label: 'Ven' },
  { value: 6, label: 'Sam' },
  { value: 7, label: 'Dim' },
];

const formatDuration = (sec: number | null): string => {
  if (!sec) return '';
  const m = Math.round(sec / 60);
  return `${m} min`;
};

interface Slot {
  key: string;
  linkKey: string | null;
  members: Placement[];
}

// Group a cell's placements into slots: an enchaînement is a group sharing a
// non-null linkKey; every null-linkKey placement is its own independent slot.
const groupSlots = (placements: Placement[], info: Record<string, WorkoutInfo>): Slot[] => {
  const slots: Slot[] = [];
  const byLink = new Map<string, Slot>();
  for (const p of placements) {
    if (p.linkKey) {
      let slot = byLink.get(p.linkKey);
      if (!slot) {
        slot = { key: p.linkKey, linkKey: p.linkKey, members: [] };
        byLink.set(p.linkKey, slot);
        slots.push(slot);
      }
      slot.members.push(p);
    } else {
      slots.push({ key: p.uid, linkKey: null, members: [p] });
    }
  }
  // The bike always comes first in an enchaînement, as the backend stores it.
  const bikeFirst = (p: Placement) => (info[p.workoutId]?.sportIdentifier === 'CYCLING' ? 0 : 1);
  for (const slot of byLink.values()) slot.members.sort((a, b) => bikeFirst(a) - bikeFirst(b));
  return slots;
};

const seedFromSessions = (
  weeks: { weekNumber: number; sessions: CycleSession[] }[],
  formatIds: string[]
): { placements: Placement[]; info: Record<string, WorkoutInfo> } => {
  const placements: Placement[] = [];
  const info: Record<string, WorkoutInfo> = {};
  const linkKeyById = new Map<string, string>();
  for (const week of weeks) {
    for (const s of week.sessions) {
      info[s.workoutId] = {
        id: s.workoutId,
        name: s.workoutName,
        sportIdentifier: s.sportIdentifier,
        themeLabel: s.themeLabel,
        durationSec: s.durationSec,
        isTest: s.isTest,
      };
      let linkKey: string | null = null;
      if (s.linkId) {
        linkKey = linkKeyById.get(s.linkId) ?? uuid();
        linkKeyById.set(s.linkId, linkKey);
      }
      placements.push({
        uid: uuid(),
        id: s.id,
        weekNumber: s.weekNumber,
        dayOfWeek: s.dayOfWeek ?? 1,
        workoutId: s.workoutId,
        rankOrder: Object.fromEntries(
          formatIds.map((f) => [f, s.priorities.find((p) => p.formatId === f)?.priority ?? MISSING_RANK_OFFSET + placements.length])
        ),
        linkKey,
      });
    }
  }
  return { placements, info };
};

const RailCard = ({ workout, selected, onClick }: { workout: WorkoutInfo; selected: boolean; onClick: () => void }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `w:${workout.id}`,
    data: { workoutId: workout.id },
  });
  return (
    <button
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      onClick={onClick}
      style={{ borderLeftColor: sportColor(workout.sportIdentifier), borderLeftWidth: 3 }}
      className={clsx(
        'flex w-full flex-col gap-1 rounded-lg border p-2.5 text-left transition-colors',
        isDragging && 'opacity-40',
        selected
          ? 'border-brand-500 bg-brand-50 dark:bg-brand-950'
          : 'border-gray-200 bg-white hover:border-brand-300 dark:border-gray-800 dark:bg-gray-900'
      )}
    >
      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{workout.name}</span>
      <span className="flex items-center gap-2 text-xs text-gray-500">
        <Badge color="blue">{workout.themeLabel}</Badge>
        {workout.durationSec ? formatDuration(workout.durationSec) : ''}
      </span>
    </button>
  );
};

/** The ▲/▼ rank editor of one placement, shared by independent slots and enchaînement members. */
const RankControls = ({
  placements,
  info,
  p,
  format,
  moveRank,
}: {
  placements: Placement[];
  info: Record<string, WorkoutInfo>;
  p: Placement;
  format: { id: string; label: string | null };
  moveRank: (uid: string, dir: -1 | 1) => void;
}) => {
  const w = info[p.workoutId];
  const rank = rankOf(placements, info, p, format.id);
  if (!w || !rank) return null;
  return (
    <div className="flex items-center justify-between text-sm">
      <span className="text-gray-700 dark:text-gray-200">
        Rang pour {SPORT_LONG[w.sportIdentifier] ?? w.sportIdentifier}
        {format.label ? ` (format ${format.label})` : ''} cette semaine : {rank.rank} / {rank.total}
      </span>
      <span className="flex gap-1">
        <button type="button" aria-label="Monter" disabled={rank.rank === 1} onClick={() => moveRank(p.uid, -1)} className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800">▲</button>
        <button type="button" aria-label="Descendre" disabled={rank.rank === rank.total} onClick={() => moveRank(p.uid, 1)} className="rounded p-1 text-gray-400 hover:bg-gray-100 disabled:opacity-30 dark:hover:bg-gray-800">▼</button>
      </span>
    </div>
  );
};

const RankBadge = ({ all, info, p, formatId }: { all: Placement[]; info: Record<string, WorkoutInfo>; p: Placement; formatId: string }) => {
  const w = info[p.workoutId];
  if (!w) return null;
  const sport = SPORT_SHORT[w.sportIdentifier] ?? w.sportIdentifier;
  const r = rankOf(all, info, p, formatId);
  if (w.isTest) return <Badge color="yellow">{r ? `${sport} · Test · P${r.rank}` : `${sport} · Test`}</Badge>;
  return <Badge color="gray">{r ? `${sport} · P${r.rank}` : sport}</Badge>;
};

/** A placed séance (or a whole enchaînement) can be dragged to another day or week. */
const DraggableSlot = ({ slot, className, children }: { slot: Slot; className: string; children: ReactNode }) => {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: `p:${slot.key}`,
    data: { uids: slot.members.map((m) => m.uid), workoutId: slot.members[0].workoutId },
  });
  return (
    <div ref={setNodeRef} {...listeners} {...attributes} className={clsx(className, 'cursor-grab', isDragging && 'opacity-40')}>
      {children}
    </div>
  );
};

const DayCell = ({
  week,
  day,
  placements,
  all,
  info,
  formatId,
  selected,
  onSelect,
}: {
  week: number;
  day: number;
  placements: Placement[];
  all: Placement[];
  info: Record<string, WorkoutInfo>;
  formatId: string;
  selected: boolean;
  onSelect: () => void;
}) => {
  const { setNodeRef, isOver } = useDroppable({ id: `c:${week}:${day}`, data: { week, day } });
  const slots = groupSlots(placements, info);

  const memberLine = (p: Placement) => (
    <span key={p.uid} className="block truncate font-medium text-gray-800 dark:text-gray-100">
      {info[p.workoutId]?.name ?? '—'}
      <span className="ml-1 font-normal text-gray-400">{formatDuration(info[p.workoutId]?.durationSec ?? null)}</span>
    </span>
  );

  return (
    <button
      ref={setNodeRef}
      type="button"
      onClick={onSelect}
      className={clsx(
        'flex min-h-[3rem] w-full flex-col gap-1.5 rounded-md border border-dashed p-1.5 text-left text-xs transition-colors',
        isOver && 'border-brand-500 bg-brand-50 dark:bg-brand-950',
        selected ? 'border-brand-500 ring-1 ring-brand-500/40' : 'border-gray-300 dark:border-gray-700',
        placements.length === 0 && 'items-center justify-center text-gray-400'
      )}
    >
      {placements.length === 0 ? (
        <span>Déposer / cliquer</span>
      ) : (
        slots.map((slot) =>
          slot.linkKey ? (
            <DraggableSlot key={slot.key} slot={slot} className="rounded border border-success-500/60 bg-success-500/5 p-1">
              <Badge color="green">Enchaînement · compte pour 1 vélo et 1 course</Badge>
              {slot.members.map((m) => (
                <div key={m.uid}>
                  {memberLine(m)}
                  <RankBadge all={all} info={info} p={m} formatId={formatId} />
                </div>
              ))}
            </DraggableSlot>
          ) : (
            <DraggableSlot key={slot.key} slot={slot} className="border-l-2 border-gray-200 pl-1.5 dark:border-gray-700">
              {slot.members.map(memberLine)}
              <RankBadge all={all} info={info} p={slot.members[0]} formatId={formatId} />
            </DraggableSlot>
          )
        )
      )}
    </button>
  );
};

const SessionsBuilder = ({ cycle }: { cycle: CycleDetail }) => {
  const { data, isLoading, saveSessions, isSaving } = useCycleSessions(cycle.id);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const [placements, setPlacements] = useState<Placement[]>([]);
  const [info, setInfo] = useState<Record<string, WorkoutInfo>>({});
  const [selected, setSelected] = useState<{ week: number; day: number } | null>(null);
  const [activeWorkoutId, setActiveWorkoutId] = useState<string | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [dirty, setDirty] = useState(false);
  const [activeFormatId, setActiveFormatId] = useState('');
  const [linked, setLinked] = useState(true);
  const [moveTarget, setMoveTarget] = useState<{ key: string; week: number; day: number } | null>(null);

  const { data: formats = [] } = useFormats();
  const groups = useFormatGroups();
  const formatKey = formats.map((f) => f.id).join(',');
  const formatIds = useMemo(() => (formatKey ? formatKey.split(',') : []), [formatKey]);
  const formatId = formatIds.includes(activeFormatId) ? activeFormatId : formatIds[0] ?? '';
  const formatLabel = formats.find((f) => f.id === formatId)?.slug ?? null;
  const activeGroup = groups.find((g) => g.formats.some((f) => f.id === formatId)) ?? groups[0];
  /** The formats an edit applies to: all of them while the orders are linked. */
  const editedFormats = linked ? formatIds : [formatId];

  useEffect(() => {
    if (!data || formatIds.length === 0) return;
    const seeded = seedFromSessions(data.weeks, formatIds);
    setPlacements(seeded.placements);
    setInfo((prev) => ({ ...seeded.info, ...prev }));
    setLinked(sameOrderInAllFormats(seeded.placements, seeded.info, formatIds));
    setDirty(false);
  }, [data, formatIds]);

  const { data: rail } = useQuery({
    queryKey: ['admin', 'pv2', 'cycle-rail', cycle.id, search],
    queryFn: () =>
      getWorkouts({ sportIds: cycle.sports.length ? cycle.sports : undefined, search: search || undefined, pageSize: 100 }),
  });

  const railWorkouts: WorkoutInfo[] = useMemo(
    () =>
      (rail?.items ?? []).map((w) => ({
        id: w.id,
        name: w.name,
        sportIdentifier: w.sportIdentifier,
        themeLabel: w.themeLabel,
        durationSec: null,
        isTest: w.isTest,
      })),
    [rail]
  );

  useEffect(() => {
    if (railWorkouts.length === 0) return;
    setInfo((prev) => {
      const next = { ...prev };
      for (const w of railWorkouts) next[w.id] ??= w;
      return next;
    });
  }, [railWorkouts]);

  const maxWeek = data?.maxWeekNumber ?? cycle.maxWeekNumber;
  const weekNumbers = useMemo(() => Array.from({ length: maxWeek }, (_, i) => i + 1), [maxWeek]);

  /** Week badges: the formats of the race type on screen, in reference order. */
  const badgeFormats = linked ? formats : activeGroup?.formats ?? [];

  const cellPlacements = (week: number, day: number): Placement[] =>
    placements.filter((p) => p.weekNumber === week && p.dayOfWeek === day);

  const mutate = (fn: (prev: Placement[]) => Placement[]) => {
    setPlacements(fn);
    setDirty(true);
  };

  const nextRankOrder = (prev: Placement[], week: number): Record<string, number> =>
    Object.fromEntries(
      formatIds.map((f) => [f, prev.filter((p) => p.weekNumber === week).reduce((max, p) => Math.max(max, p.rankOrder[f] ?? 0), 0) + 1])
    );

  // Linking copies the order shown to every format.
  const toggleLinked = (next: boolean) => {
    setLinked(next);
    if (next && !sameOrderInAllFormats(placements, info, formatIds)) {
      mutate((prev) =>
        prev.map((p) => ({ ...p, rankOrder: Object.fromEntries(formatIds.map((f) => [f, p.rankOrder[formatId] ?? 0])) }))
      );
    }
  };

  const refuseSecondTest = () =>
    toaster.create({ title: 'Un seul test par discipline et par semaine', type: 'error' });

  // A new séance joins the week as the last rank of its sport, a test included (D26).
  const placeWorkout = (week: number, day: number, workoutId: string) => {
    const w = info[workoutId];
    if (w?.isTest && hasTestOfSport(placements, info, week, w.sportIdentifier)) return refuseSecondTest();
    mutate((prev) => [...prev, { uid: uuid(), weekNumber: week, dayOfWeek: day, workoutId, rankOrder: nextRankOrder(prev, week), linkKey: null }]);
  };

  // Changing the workout to another sport leaves the old ranking and joins the new one last.
  const setPlacementWorkout = (uid: string, workoutId: string) => {
    if (!workoutId) return;
    const target = placements.find((p) => p.uid === uid);
    const w = info[workoutId];
    if (target && w?.isTest && hasTestOfSport(placements, info, target.weekNumber, w.sportIdentifier, uid)) return refuseSecondTest();
    mutate((prev) =>
      prev.map((p) => {
        if (p.uid !== uid) return p;
        const sameSport = info[p.workoutId]?.sportIdentifier === info[workoutId]?.sportIdentifier;
        return { ...p, workoutId, rankOrder: sameSport ? p.rankOrder : nextRankOrder(prev, p.weekNumber) };
      })
    );
  };

  // ▲ / ▼ swap the insertion order with the neighbour of the same sport, in the edited formats.
  const moveRank = (uid: string, dir: -1 | 1) =>
    mutate((prev) => {
      const p = prev.find((x) => x.uid === uid);
      const w = p && info[p.workoutId];
      if (!p || !w) return prev;
      let next = prev;
      for (const f of editedFormats) {
        const ranked = rankedOfSport(next, info, p.weekNumber, w.sportIdentifier, f);
        const i = ranked.findIndex((x) => x.uid === uid);
        const j = i + dir;
        if (i < 0 || j < 0 || j >= ranked.length) continue;
        const a = ranked[i];
        const b = ranked[j];
        next = next.map((x) =>
          x.uid === a.uid
            ? { ...x, rankOrder: { ...x.rankOrder, [f]: b.rankOrder[f] } }
            : x.uid === b.uid
              ? { ...x, rankOrder: { ...x.rankOrder, [f]: a.rankOrder[f] } }
              : x
        );
      }
      return next;
    });

  /**
   * PRD admin-08 §2: move a séance (an enchaînement as one unit) in place. Its id, its links and,
   * within the same week, its ranks are kept; moved to another week it joins that week last.
   */
  const moveSlot = (uids: string[], week: number, day: number) => {
    const members = placements.filter((p) => uids.includes(p.uid));
    if (members.length === 0) return;
    if (week < 1 || week > maxWeek) {
      toaster.create({ title: `Semaine hors du cycle (max ${maxWeek})`, type: 'error' });
      return;
    }
    const fromWeek = members[0].weekNumber;
    if (week !== fromWeek) {
      const test = members.find((m) => info[m.workoutId]?.isTest);
      if (test && hasTestOfSport(placements, info, week, info[test.workoutId].sportIdentifier)) return refuseSecondTest();
      if (members[0].linkKey && placements.some((p) => p.weekNumber === week && p.linkKey && p.linkKey !== members[0].linkKey)) {
        toaster.create({ title: 'Un seul enchaînement par semaine', type: 'error' });
        return;
      }
    }
    mutate((prev) => {
      const rankOrder = week === fromWeek ? null : nextRankOrder(prev.filter((p) => !uids.includes(p.uid)), week);
      return prev.map((p) => (uids.includes(p.uid) ? { ...p, weekNumber: week, dayOfWeek: day, rankOrder: rankOrder ?? p.rankOrder } : p));
    });
    if (selected) setSelected({ week, day });
  };

  const removeSlot = (uids: string[]) => mutate((prev) => prev.filter((p) => !uids.includes(p.uid)));

  // Link a second séance to an existing independent placement → enchaînement.
  const linkSecond = (primaryUid: string, workoutId: string) => {
    mutate((prev) => {
      const primary = prev.find((p) => p.uid === primaryUid);
      if (!primary) return prev;
      if (!workoutId) {
        // un-pair: drop the link on this group, keep placements independent.
        return primary.linkKey
          ? prev.map((p) => (p.linkKey === primary.linkKey ? { ...p, linkKey: null } : p))
          : prev;
      }
      const linkKey = primary.linkKey ?? uuid();
      const existingSecond = prev.find((p) => p.linkKey === linkKey && p.uid !== primaryUid);
      const withPrimary = prev.map((p) => (p.uid === primaryUid ? { ...p, linkKey } : p));
      if (existingSecond) {
        return withPrimary.map((p) => (p.uid === existingSecond.uid ? { ...p, workoutId, linkKey } : p));
      }
      return [
        ...withPrimary,
        {
          uid: uuid(),
          weekNumber: primary.weekNumber,
          dayOfWeek: primary.dayOfWeek,
          workoutId,
          rankOrder: { ...primary.rankOrder },
          linkKey,
        },
      ];
    });
  };

  const clearWeek = (week: number) => mutate((prev) => prev.filter((p) => p.weekNumber !== week));

  /** Replaces « Copier A→B »: the pair is gone, so the useful shortcut is the previous week. */
  const copyPreviousWeek = (week: number) => {
    if (week <= 1) return;
    mutate((prev) => {
      const kept = prev.filter((p) => p.weekNumber !== week);
      const source = prev.filter((p) => p.weekNumber === week - 1);
      const linkRemap = new Map<string, string>();
      const clones = source.map((p) => {
        let linkKey = p.linkKey;
        if (linkKey) {
          linkKey = linkRemap.get(linkKey) ?? uuid();
          linkRemap.set(p.linkKey!, linkKey);
        }
        return { ...p, uid: uuid(), id: undefined, weekNumber: week, linkKey };
      });
      return [...kept, ...clones];
    });
  };

  const onDragStart = (e: DragStartEvent) => setDragging((e.active.data.current?.workoutId as string) ?? null);
  const onDragEnd = (e: DragEndEvent) => {
    setDragging(null);
    const uids = e.active.data.current?.uids as string[] | undefined;
    const workoutId = e.active.data.current?.workoutId as string | undefined;
    const over = e.over?.data.current as { week: number; day: number } | undefined;
    if (!over) return;
    if (uids) moveSlot(uids, over.week, over.day);
    else if (workoutId) placeWorkout(over.week, over.day, workoutId);
  };

  const handleRailClick = (workoutId: string) => {
    if (selected) {
      placeWorkout(selected.week, selected.day, workoutId);
    } else {
      setActiveWorkoutId((prev) => (prev === workoutId ? null : workoutId));
    }
  };

  const handleSave = () => {
    const inputs: CycleSessionInput[] = placements
      .filter((p) => p.workoutId)
      .map((p) => ({
        id: p.id,
        weekNumber: p.weekNumber,
        dayOfWeek: p.dayOfWeek,
        workoutId: p.workoutId,
        priorities: formatIds.map((f) => ({ formatId: f, priority: rankOf(placements, info, p, f)?.rank ?? 1 })),
        linkKey: p.linkKey,
      }));
    saveSessions(inputs).then(() => setDirty(false)).catch(() => undefined);
  };

  const workoutOptions = (currentId?: string, excludeOptional = false) => {
    const present = railWorkouts.some((w) => w.id === currentId);
    return (
      <>
        {currentId && !present && <option value={currentId}>{info[currentId]?.name ?? '—'}</option>}
        {railWorkouts.map((w) => (
          <option
            key={w.id}
            value={w.id}
            disabled={excludeOptional && isOptionalWorkout(w)}
            title={excludeOptional && isOptionalWorkout(w) ? "Les séances de test et de PPG ne peuvent pas faire partie d'un enchaînement" : undefined}
          >
            {w.name}
            {w.isTest ? ' (test)' : ''}
          </option>
        ))}
      </>
    );
  };

  const rankFormat = { id: formatId, label: linked ? null : formatLabel };
  const selectedSlots = selected ? groupSlots(cellPlacements(selected.week, selected.day), info) : [];
  const draggingWorkout = dragging ? info[dragging] : null;

  if (isLoading) return <p className="text-sm text-gray-500">Chargement…</p>;

  return (
    <DndContext sensors={sensors} onDragStart={onDragStart} onDragEnd={onDragEnd}>
      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm text-gray-500">
          {cycle.name} — séances · {maxWeek} semaine(s)
        </p>
        <div className="flex flex-wrap items-center gap-3">
          <label className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-300">
            <Switch size="sm" checked={linked} onChange={toggleLinked} />
            Même ordre pour tous les formats
          </label>
          {!linked && groups.length > 1 && (
            <SegmentedControl
              size="sm"
              value={String(activeGroup?.raceType.id ?? '')}
              options={groups.map((g) => ({ value: String(g.raceType.id), label: g.raceType.label }))}
              onChange={(v) => setActiveFormatId(groups.find((g) => String(g.raceType.id) === v)?.formats[0]?.id ?? '')}
            />
          )}
          {!linked && activeGroup && activeGroup.formats.length > 1 && (
            <SegmentedControl
              size="sm"
              value={formatId}
              options={activeGroup.formats.map((f) => ({ value: f.id, label: f.slug }))}
              onChange={setActiveFormatId}
            />
          )}
          <Button onClick={handleSave} disabled={isSaving || !dirty || formatIds.length === 0}>
            {isSaving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
        </div>
      </div>
      {!linked && (
        <p className="mb-3 text-xs text-gray-400">
          Les rangs affichés et modifiés sont ceux du format {formatLabel}. Réactiver « Même ordre pour tous les formats » recopie
          cet ordre dans tous les formats.
        </p>
      )}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[240px_1fr_260px]">
        {/* Library rail */}
        <Card className="flex max-h-[70vh] flex-col gap-3 overflow-hidden">
          <Input placeholder="Rechercher une séance" value={search} onChange={(e) => setSearch(e.target.value)} />
          <p className="text-xs text-gray-400">Glissez une carte dans un créneau, ou cliquez un créneau puis une carte.</p>
          <div className="flex flex-col gap-2 overflow-y-auto pr-1">
            {railWorkouts.length === 0 && <p className="text-sm text-gray-400">Aucune séance.</p>}
            {railWorkouts.map((w) => (
              <RailCard key={w.id} workout={w} selected={activeWorkoutId === w.id} onClick={() => handleRailClick(w.id)} />
            ))}
          </div>
        </Card>

        {/* Week grid */}
        <div className="flex max-h-[70vh] flex-col gap-4 overflow-y-auto pr-1">
          {weekNumbers.map((week) => {
            // R42: a week with no session is a rest week the coach decided, and the builder says so
            // out loud — that visibility is what replaces the WEEK_SOURCE_MISSING warning.
            const isRestWeek = placements.every((p) => p.weekNumber !== week);
            return (
              <Card key={week} className="flex flex-col gap-3">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-gray-900 dark:text-gray-100">Semaine {week}</span>
                    {isRestWeek && <Badge color="yellow">Semaine de repos</Badge>}
                    {badgeFormats.map((f) => {
                      const wp = cycle.weekPriorities.find((x) => x.formatId === f.id && x.weekNumber === week);
                      return wp ? (
                        <Badge key={f.id} color="blue">
                          {f.slug} · rang {wp.priority}
                        </Badge>
                      ) : null;
                    })}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button size="sm" variant="ghost" onClick={() => clearWeek(week)}>
                      Vider
                    </Button>
                    {week > 1 && (
                      <Button size="sm" variant="outline" onClick={() => copyPreviousWeek(week)}>
                        <ArrowRightIcon className="h-4 w-4" /> Copier la semaine {week - 1}
                      </Button>
                    )}
                  </div>
                </div>

                <div className="flex flex-col gap-1.5">
                  {DAYS.map((d) => (
                    <div key={d.value} className="flex items-center gap-2">
                      <span className="w-8 shrink-0 text-[11px] uppercase text-gray-400">{d.label}</span>
                      <div className="flex-1">
                        <DayCell
                          week={week}
                          day={d.value}
                          placements={cellPlacements(week, d.value)}
                          all={placements}
                          info={info}
                          formatId={formatId}
                          selected={selected?.week === week && selected?.day === d.value}
                          onSelect={() => setSelected({ week, day: d.value })}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </Card>
            );
          })}
        </div>

        {/* Detail panel */}
        <Card className="max-h-[70vh] overflow-y-auto">
          {!selected ? (
            <p className="text-sm text-gray-400">Sélectionnez un créneau pour éditer la séance.</p>
          ) : (
            <div className="flex flex-col gap-4">
              <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                Semaine {selected.week} · {DAYS.find((d) => d.value === selected.day)?.label}
              </p>

              {selectedSlots.length === 0 && (
                <p className="text-xs text-gray-400">Aucune séance. Ajoutez-en une ci-dessous.</p>
              )}

              {selectedSlots.map((slot) => {
                const primary = slot.members[0];
                const second = slot.members[1];
                const memberUids = slot.members.map((m) => m.uid);
                const primaryInfo = info[primary.workoutId];
                const rank = rankOf(placements, info, primary, formatId);
                const target = moveTarget?.key === slot.key ? moveTarget : { key: slot.key, week: primary.weekNumber, day: primary.dayOfWeek };
                return (
                  <div
                    key={slot.key}
                    className={clsx(
                      'flex flex-col gap-2 rounded-lg border p-2.5',
                      slot.linkKey
                        ? 'border-success-500/60 bg-success-500/5'
                        : 'border-gray-200 dark:border-gray-800'
                    )}
                  >
                    {slot.linkKey && <Badge color="green">Enchaînement</Badge>}

                    <Select
                      label="Séance"
                      value={primary.workoutId}
                      onChange={(e) => setPlacementWorkout(primary.uid, e.target.value)}
                    >
                      <option value="">— Choisir —</option>
                      {workoutOptions(primary.workoutId, Boolean(slot.linkKey))}
                    </Select>

                    {!slot.linkKey && primaryInfo?.isTest && (
                      <p className="text-xs text-gray-500">
                        Séance de test : son rang est libre par format. Classée après les N séances de l'athlète, elle
                        n'apparaît pas — désactivez « Même ordre pour tous les formats » pour la classer bas sur un seul format.
                      </p>
                    )}
                    {!slot.linkKey && primaryInfo && rank && (
                      <div className="flex flex-col gap-1">
                        <RankControls placements={placements} info={info} p={primary} format={rankFormat} moveRank={moveRank} />
                        <p className="text-xs text-gray-400">
                          L'athlète fait les N premières séances de chaque discipline, N étant le nombre qu'il a choisi.
                        </p>
                        {primaryInfo.sportIdentifier === PPG && <p className="text-xs text-gray-400">Incluse si l'athlète choisit la PPG.</p>}
                      </div>
                    )}

                    {!isOptionalWorkout(primaryInfo) && (
                      <Select
                        label="Enchaînement (2ᵉ séance)"
                        value={second?.workoutId ?? ''}
                        onChange={(e) => linkSecond(primary.uid, e.target.value)}
                      >
                        <option value="">— Aucun —</option>
                        {workoutOptions(second?.workoutId, true)}
                      </Select>
                    )}
                    {slot.linkKey && (
                      <div className="flex flex-col gap-1">
                        {slot.members.map((m) => (
                          <RankControls key={m.uid} placements={placements} info={info} p={m} format={rankFormat} moveRank={moveRank} />
                        ))}
                        <p className="text-xs text-gray-400">
                          Le vélo passe avant la course à pied. Si la place manque en course, l'enchaînement devient une séance de vélo
                          seule ; si elle manque en vélo, il n'a pas lieu.
                        </p>
                      </div>
                    )}

                    <div className="flex items-end gap-2">
                      <Select
                        label="Déplacer vers"
                        value={target.week}
                        onChange={(e) => setMoveTarget({ ...target, week: Number(e.target.value) })}
                      >
                        {weekNumbers.map((w) => (
                          <option key={w} value={w}>
                            Semaine {w}
                          </option>
                        ))}
                      </Select>
                      <Select
                        value={target.day}
                        onChange={(e) => setMoveTarget({ ...target, day: Number(e.target.value) })}
                      >
                        {DAYS.map((d) => (
                          <option key={d.value} value={d.value}>
                            {d.label}
                          </option>
                        ))}
                      </Select>
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={target.week === primary.weekNumber && target.day === primary.dayOfWeek}
                        onClick={() => {
                          moveSlot(memberUids, target.week, target.day);
                          setMoveTarget(null);
                        }}
                      >
                        Déplacer
                      </Button>
                    </div>

                    <Button variant="danger" size="sm" onClick={() => removeSlot(memberUids)}>
                      <TrashIcon className="h-4 w-4" /> Retirer
                    </Button>
                  </div>
                );
              })}

              <Button variant="outline" size="sm" onClick={() => placeWorkout(selected.week, selected.day, '')}>
                <PlusIcon className="h-4 w-4" /> Ajouter une séance
              </Button>
              <p className="text-xs text-gray-400">
                Le rang classe les séances d'un même sport dans la semaine : la P1 est toujours conservée, les suivantes
                sont ajoutées selon les jours de l'athlète. Le jour sert à ordonner les séances d'un même sport ; il est
                obligatoire pour les plans à jours fixes. Un test se classe comme les autres séances ; les deux séances d'un
                enchaînement sont classées dans leur propre sport. Glissez une séance posée pour la déplacer : elle garde ses
                rangs et son enchaînement.
              </p>
            </div>
          )}
        </Card>
      </div>

      <DragOverlay>
        {draggingWorkout && (
          <div className="rounded-lg border border-brand-500 bg-white p-2 text-sm font-medium shadow-lg dark:bg-gray-900">
            {draggingWorkout.name}
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
};

export default SessionsBuilder;
